import { Controller } from "@hotwired/stimulus"

// The video stage: one camera feed fills the screen ("main"), the others sit in a
// corner picture-in-picture rail. Clicking a PiP tile promotes it to main; dragging one
// lifts it out of the rail into a free-floating position, and its corner grip resizes it
// (width-driven — the CSS aspect-ratio keeps the feed's shape). Main-feed choice and
// floating rects persist per robot.
//
// Deliberately decoupled from beam_teleop_controller.js: this controller only relocates
// the existing feed-tile DOM nodes between the main slot, the PiP rail and the stage
// layer. The tiles still carry their data-beam-teleop-target nodes (video, placeholders,
// buttons), which resolve to the outer beam-teleop controller regardless of where the
// tile lives.
//
// Two side effects after a move:
//   - re-play() the <video> elements (moving a media element in the DOM can pause it)
//   - dispatch a window "resize" so the Theta three.js viewer re-fits its new container
//     (its initThetaViewer already listens for window resize).
export default class extends Controller {
  static targets = ["mainSlot", "pipRail", "tile"]
  static values = { robotId: String }

  // Keep a moved tile grabbable: it must stay below the top bar and inside the stage.
  // Width bounds so a resized tile never collapses or covers the whole console.
  // Mirrors beam_teleop_layout_controller.js, which owns the same numbers for widgets.
  static TOPBAR = 56
  static EDGE = 24
  static MIN_W = 160
  static DRAG_THRESHOLD = 3

  connect() {
    // Remember each tile's rail order so reset can re-dock them as authored.
    this.tileTargets.forEach((tile, index) => {
      tile._homeIndex = index
    })
    const stored = this.read()
    const feed = this.tileTargets.some((t) => t.dataset.feed === stored)
      ? stored
      : this.currentMain()
    this.setMain(feed)
    this.restoreTiles()
    this._onReset = () => this.redockTiles()
    this._onWindowResize = () => this.clampAllTiles()
    window.addEventListener("beam-teleop:reset-layout", this._onReset)
    window.addEventListener("resize", this._onWindowResize)
  }

  disconnect() {
    window.removeEventListener("beam-teleop:reset-layout", this._onReset)
    window.removeEventListener("resize", this._onWindowResize)
    this.unbindPointer()
  }

  get storageKey() {
    return `beam-teleop-mainfeed:${this.robotIdValue}`
  }

  get tilesStorageKey() {
    return `beam-teleop-tiles:${this.robotIdValue}`
  }

  currentMain() {
    const main = this.tileTargets.find((t) => t.dataset.position === "main")
    return main ? main.dataset.feed : this.tileTargets[0]?.dataset.feed
  }

  // Click handler on each tile. A tile that is already main is a no-op (lets its inner
  // controls — listen, snapshot, theta drag — work normally). A PiP tile gets promoted —
  // unless the pointer-up just ended a drag (browsers still fire a click afterwards).
  promote(event) {
    const tile = event.currentTarget
    if (tile._suppressClick) {
      tile._suppressClick = false
      return
    }
    if (tile.dataset.position === "main") return
    this.setMain(tile.dataset.feed)
  }

  setMain(feed) {
    if (!this.hasMainSlotTarget || !this.hasPipRailTarget) return
    this.tileTargets.forEach((tile) => {
      const isMain = tile.dataset.feed === feed
      tile.dataset.position = isMain ? "main" : "pip"
      if (isMain) {
        this.clearFloat(tile)
        if (tile.parentElement !== this.mainSlotTarget) {
          this.mainSlotTarget.appendChild(tile)
          this.replayVideos(tile)
        }
      } else if (tile._freeRect) {
        // A demoted tile that had been floated returns to its remembered spot.
        this.floatTile(tile, tile._freeRect)
      } else if (tile.parentElement !== this.pipRailTarget) {
        this.pipRailTarget.appendChild(tile)
        this.replayVideos(tile)
      }
    })
    window.dispatchEvent(new Event("resize"))
    try {
      localStorage.setItem(this.storageKey, feed)
    } catch {
      /* private mode / quota — selection simply won't persist */
    }
  }

  // --- drag (whole PiP tile) -------------------------------------------------------
  tileDragStart(event) {
    const tile = event.currentTarget
    if (tile.dataset.position !== "pip") return
    if (event.target.closest(".teleop-tile__resize")) return
    // A twitch while pressing a tile control (Listen, Snapshot, Fullscreen) must not lift the tile.
    if (event.target.closest("button")) return
    this._active = tile
    this._mode = "drag"
    this._startX = event.clientX
    this._startY = event.clientY
    this._moved = false
    this.bindPointer()
  }

  // --- resize (corner grip) --------------------------------------------------------
  tileResizeStart(event) {
    event.preventDefault()
    event.stopPropagation()
    const tile = event.currentTarget.closest(".teleop-tile")
    if (!tile || tile.dataset.position !== "pip") return
    this.detachTile(tile)
    this._active = tile
    this._mode = "resize"
    this._moved = true
    this._startX = event.clientX
    this._startY = event.clientY
    this._origW = tile.getBoundingClientRect().width
    tile.classList.add("is-dragging")
    this.bindPointer()
  }

  // --- shared pointer lifecycle ----------------------------------------------------
  bindPointer() {
    // A second pointerdown before the matching pointerup (multi-touch) must not orphan
    // the previous pair of window listeners.
    this.unbindPointer()
    this._moveHandler = this.pointerMove.bind(this)
    this._upHandler = this.pointerUp.bind(this)
    window.addEventListener("pointermove", this._moveHandler)
    window.addEventListener("pointerup", this._upHandler)
    window.addEventListener("pointercancel", this._upHandler)
  }

  unbindPointer() {
    if (!this._moveHandler) return
    window.removeEventListener("pointermove", this._moveHandler)
    window.removeEventListener("pointerup", this._upHandler)
    window.removeEventListener("pointercancel", this._upHandler)
    this._moveHandler = null
    this._upHandler = null
  }

  pointerMove(event) {
    if (!this._active) return
    const dx = event.clientX - this._startX
    const dy = event.clientY - this._startY

    if (this._mode === "drag") {
      // Defer detaching until a real drag begins, so a plain click still promotes.
      if (!this._moved) {
        if (
          Math.abs(dx) < this.constructor.DRAG_THRESHOLD &&
          Math.abs(dy) < this.constructor.DRAG_THRESHOLD
        )
          return
        this._moved = true
        this.detachTile(this._active)
        this._origLeft = parseFloat(this._active.style.left) || 0
        this._origTop = parseFloat(this._active.style.top) || 0
        this._active.classList.add("is-dragging")
      }
      event.preventDefault()
      const { x, y } = this.clampTile(this._active, this._origLeft + dx, this._origTop + dy)
      this._active.style.left = `${x}px`
      this._active.style.top = `${y}px`
    } else {
      event.preventDefault()
      const max = this.element.clientWidth * 0.8
      const w = Math.min(max, Math.max(this.constructor.MIN_W, this._origW + dx))
      this._active.style.width = `${w}px`
      // The theta tile hosts a three.js canvas that must re-fit its container.
      if (this._active.dataset.feed === "theta") window.dispatchEvent(new Event("resize"))
    }
  }

  pointerUp() {
    const tile = this._active
    const moved = this._moved
    this.unbindPointer()
    this._active = null
    if (!tile) return
    tile.classList.remove("is-dragging")
    if (moved) {
      // The click that follows this pointer-up would promote the tile — swallow it. If
      // the pointer was released off-tile no click fires at all, so clear the flag on
      // the next tick rather than leaving it to eat a later genuine click.
      tile._suppressClick = true
      setTimeout(() => {
        tile._suppressClick = false
      }, 0)
      tile._freeRect = {
        x: parseFloat(tile.style.left) || 0,
        y: parseFloat(tile.style.top) || 0,
        w: tile.offsetWidth,
      }
      this.persistTiles()
    }
  }

  // --- free-floating helpers -------------------------------------------------------
  // Lift a docked PiP tile out of the rail into the stage layer, pinned to its current
  // on-screen position so it does not jump.
  detachTile(tile) {
    if (tile._free) return
    const rect = tile.getBoundingClientRect()
    const base = this.element.getBoundingClientRect()
    this.floatTile(tile, { x: rect.left - base.left, y: rect.top - base.top, w: rect.width })
  }

  floatTile(tile, { x, y, w }) {
    const reparent = tile.parentElement !== this.element
    if (reparent) this.element.appendChild(tile)
    tile.classList.add("teleop-tile--free")
    // Width first: clampTile reads offsetWidth, which the rail width no longer governs.
    tile.style.width = `${w}px`
    const p = this.clampTile(tile, x, y)
    tile.style.left = `${p.x}px`
    tile.style.top = `${p.y}px`
    tile._free = true
    tile._freeRect = { x: p.x, y: p.y, w }
    if (reparent) this.replayVideos(tile)
  }

  // Promotion to main: drop the float so the CSS inset:0 can fill the screen again.
  // _freeRect is deliberately kept, so a later demote restores the floating spot.
  clearFloat(tile) {
    tile.classList.remove("teleop-tile--free", "is-dragging")
    tile.style.left = tile.style.top = tile.style.width = ""
    tile._free = false
  }

  // Keep the tile on-screen and below the top bar so a moved tile stays grabbable.
  clampTile(tile, x, y) {
    const maxX = this.element.clientWidth - this.constructor.EDGE
    const maxY = this.element.clientHeight - this.constructor.EDGE
    const minX = this.constructor.EDGE - tile.offsetWidth
    return {
      x: Math.min(Math.max(x, minX), maxX),
      y: Math.min(Math.max(y, this.constructor.TOPBAR), maxY),
    }
  }

  clampAllTiles() {
    this.tileTargets.forEach((tile) => {
      if (!tile._free) return
      const { x, y } = this.clampTile(
        tile,
        parseFloat(tile.style.left) || 0,
        parseFloat(tile.style.top) || 0,
      )
      tile.style.left = `${x}px`
      tile.style.top = `${y}px`
    })
  }

  // Reset (broadcast by the layout controller's reset button): forget every floating
  // rect and re-dock the PiP tiles into the rail in their authored order.
  redockTiles() {
    this.tileTargets.forEach((tile) => {
      this.clearFloat(tile)
      delete tile._freeRect
    })
    this.tileTargets
      .slice()
      .sort((a, b) => a._homeIndex - b._homeIndex)
      .forEach((tile) => {
        if (tile.dataset.position !== "pip") return
        this.pipRailTarget.appendChild(tile)
        this.replayVideos(tile)
      })
    try {
      localStorage.removeItem(this.tilesStorageKey)
    } catch {
      /* private mode / quota */
    }
    window.dispatchEvent(new Event("resize"))
  }

  // Moving a media element in the DOM can pause it — resume after any re-parent.
  replayVideos(tile) {
    tile.querySelectorAll("video").forEach((v) => {
      const playing = v.play()
      if (playing) playing.catch(() => {})
    })
  }

  // --- persistence -----------------------------------------------------------------
  // { "<feed>": { x, y, w } } — a feed's presence means it has a remembered floating
  // rect. The current main's rect is kept too, so its float survives promote + reload.
  persistTiles() {
    const state = {}
    this.tileTargets.forEach((tile) => {
      if (tile._freeRect) state[tile.dataset.feed] = tile._freeRect
    })
    try {
      localStorage.setItem(this.tilesStorageKey, JSON.stringify(state))
    } catch {
      /* private mode / quota — layout simply won't persist */
    }
  }

  restoreTiles() {
    let state
    try {
      state = JSON.parse(localStorage.getItem(this.tilesStorageKey) || "null")
    } catch {
      state = null
    }
    if (!state) return
    this.tileTargets.forEach((tile) => {
      const saved = state[tile.dataset.feed]
      if (!saved) return
      tile._freeRect = saved
      if (tile.dataset.position === "pip") this.floatTile(tile, saved)
    })
  }

  read() {
    try {
      return localStorage.getItem(this.storageKey)
    } catch {
      return null
    }
  }
}
